// Bacha Way Travel — unified fleet catalogue (display + booking)
import vEconomy from "@/assets/v-economy.webp";
import vFamily from "@/assets/v-family.webp";
import vPremiumSedan from "@/assets/v-premium-sedan.webp";
import vLarge from "@/assets/v-large.webp";
import vPremiumVan from "@/assets/v-premium-van.webp";
import vMinibus from "@/assets/v-minibus.webp";
import vLuxury from "@/assets/v-luxury.webp";

export type FleetId =
  | "economy"
  | "premium-sedan"
  | "family"
  | "premium-van"
  | "large"
  | "minibus"
  | "luxury"
  | "bus";

/**
 * Top-level vehicle hierarchy presented to customers and used by the
 * search/pricing UI as filter chips.
 *  - sedan   : 1–3 pax, standard luggage — quick & executive transfers
 *  - minivan : 4–6 pax, custom surfboard/sports layout options
 *  - van     : 7–15 pax, premium VIP (Sprinter / Tourneo) configurations
 *  - bus     : 16–56 pax, tourist groups, events and logistics
 */
export type FleetCategory = "sedan" | "minivan" | "van" | "bus";

export interface FleetVehicle {
  id: FleetId;
  name: string;
  /** Sub-type label, e.g. "Compact Sedan", "MPV", "Premium Van". */
  type: string;
  /** Top-level customer-facing category — used by the booking UI filter. */
  category: FleetCategory;
  /** Representative models (driver assigns at confirmation). */
  model: string;
  image: string;
  pax: number;
  bags: number;
  description: string;
  /** Approximate per-km rate (MAD) used for the Jony-style result cards. */
  perKm: number;
  /** Base fare (MAD). */
  base: number;
}

export const FLEET: FleetVehicle[] = [
  {
    id: "economy",
    name: "Economy Sedan",
    type: "Compact Sedan",
    category: "sedan",
    model: "Dacia Logan, Renault Symbol or similar",
    image: vEconomy,
    pax: 3,
    bags: 3,
    description: "Air-conditioned compact sedan (1–3 pax). Reliable everyday transfers at the best price.",
    base: 90,
    perKm: 6,
  },
  {
    id: "premium-sedan",
    name: "Premium Sedan",
    type: "Executive Sedan",
    category: "sedan",
    model: "Mercedes-Benz E-Class, BMW 5 Series or similar",
    image: vPremiumSedan,
    pax: 3,
    bags: 3,
    description: "Executive sedan with leather interior (1–3 pax). Business-class on the road.",
    base: 150,
    perKm: 9,
  },
  {
    id: "luxury",
    name: "Luxury Sedan",
    type: "Luxury Sedan",
    category: "sedan",
    model: "Mercedes-Benz S-Class, BMW 7 Series or similar",
    image: vLuxury,
    pax: 3,
    bags: 3,
    description: "Flagship Mercedes S-Class for VIPs and special occasions (1–3 pax).",
    base: 260,
    perKm: 14,
  },
  {
    id: "family",
    name: "Family Minivan",
    type: "MPV / Minivan",
    category: "minivan",
    model: "Dacia Lodgy, Citroën Berlingo or similar",
    image: vFamily,
    pax: 6,
    bags: 5,
    description: "Spacious MPV (4–6 pax) with custom layout options for surfers and long surfboards — ideal for Tamraght & Imsouane runs.",
    base: 110,
    perKm: 7,
  },
  {
    id: "large",
    name: "Surf Minivan",
    type: "Surf-ready Minivan",
    category: "minivan",
    model: "Renault Trafic, Hyundai H1 or similar",
    image: vLarge,
    pax: 6,
    bags: 6,
    description: "Surf-equipped minivan (4–6 pax) with rear rack & interior board storage for longboards and heavy sports gear.",
    base: 170,
    perKm: 10,
  },
  {
    id: "premium-van",
    name: "VIP Van (Mercedes V-Class)",
    type: "Premium VIP Van",
    category: "van",
    model: "Mercedes-Benz V-Class / Vito VIP or similar",
    image: vPremiumVan,
    pax: 7,
    bags: 7,
    description: "Premium VIP van (7–8 pax) with leather seating, dual climate control and tinted windows.",
    base: 220,
    perKm: 12,
  },
  {
    id: "minibus",
    name: "VIP Sprinter Van",
    type: "Premium Van",
    category: "van",
    model: "Mercedes-Benz Sprinter, Ford Tourneo Custom or similar",
    image: vMinibus,
    pax: 15,
    bags: 15,
    description: "Flagship Mercedes Sprinter / Ford Tourneo Custom (up to 15 pax) — dual climate control, leather seating and maximum comfort for VIP groups.",
    base: 280,
    perKm: 14,
  },
  {
    id: "bus",
    name: "Tourist Bus",
    type: "Coach / Bus",
    category: "bus",
    model: "Mercedes-Benz Tourismo, MAN Lion's Coach or similar",
    image: vMinibus,
    pax: 56,
    bags: 56,
    description: "High-capacity coach (up to 56 pax) for tourist groups, events and logistics operations.",
    base: 900,
    perKm: 22,
  },
];

export const FLEET_CATEGORIES: Array<{
  id: FleetCategory;
  label: string;
  paxRange: string;
  blurb: string;
}> = [
  { id: "sedan",   label: "Sedan",   paxRange: "1–3 pax",  blurb: "Quick, cost-effective or executive airport transfers." },
  { id: "minivan", label: "Minivan", paxRange: "4–6 pax",  blurb: "Custom surf & sports-gear layouts for Tamraght / Imsouane." },
  { id: "van",     label: "Van",     paxRange: "7–15 pax", blurb: "Premium VIP Mercedes Sprinter / Ford Tourneo configurations." },
  { id: "bus",     label: "Bus",     paxRange: "16–56 pax", blurb: "Large tourist groups, events and logistics." },
];

export function estimateFleetPrice(vehicle: FleetVehicle, distanceKm: number): number {
  const subtotal = vehicle.base + vehicle.perKm * Math.max(0, distanceKm);
  return Math.max(105, Math.round(subtotal));
}

